import React, { useState, useEffect, useContext, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import whatsappIcon from '../../assets/nopicture.png'
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import useWhatsApps from "../../hooks/useWhatsApps";

import { Can } from "../Can";
import { Avatar, Grid, Input, Paper, Tab, Tabs, Checkbox, Typography } from "@material-ui/core";
import { getBackendUrl } from "../../config";
import TabPanel from "../TabPanel";
import AvatarUploader from "../AvatarUpload";

const backendUrl = getBackendUrl();

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": {
			marginRight: theme.spacing(1),
		},
	},
	btnWrapper: {
		position: "relative",
	},
	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	formControl: {
		margin: theme.spacing(1),
		minWidth: 120,
	},
	textField: {
		marginRight: theme.spacing(1),
		flex: 1,
	},
	container: {
		display: 'flex',
		flexWrap: 'wrap',
	},
	avatar: {
		width: theme.spacing(12),
		height: theme.spacing(12),
		margin: theme.spacing(2),
		cursor: 'pointer',
		borderRadius: '50%',
		border: '2px solid #ccc',
	},
	updateDiv: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
	},
	updateInput: {
		display: 'none',
	},
	updateLabel: {
		padding: theme.spacing(1),
		margin: theme.spacing(1),
		textTransform: 'uppercase',
		textAlign: 'center',
		cursor: 'pointer',
		border: '2px solid #ccc',
		borderRadius: '5px',
		minWidth: 160,
		fontWeight: 'bold',
		color: '#555',
	},
	errorUpdate: {
		border: '2px solid red',
	},
	errorText: {
		color: 'red',
		fontSize: '0.8rem',
		fontWeight: 'bold',
	},
	permissionsPanel: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(1),
		padding: theme.spacing(1, 0, 2),
	},
	permissionRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: theme.spacing(1.25, 1.5),
		borderRadius: theme.spacing(1),
		border: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.background.default,
		transition: "background-color 0.15s ease, border-color 0.15s ease",
		"&:hover": {
			backgroundColor: theme.palette.action.hover,
		},
	},
	permissionRowActive: {
		borderColor: theme.palette.primary.light,
		backgroundColor: theme.palette.type === "dark"
			? "rgba(25, 118, 210, 0.08)"
			: "rgba(25, 118, 210, 0.04)",
	},
	permissionLabel: {
		flex: 1,
		paddingRight: theme.spacing(2),
	},
	permissionTitle: {
		fontSize: "0.875rem",
		fontWeight: 600,
		lineHeight: 1.4,
	},
	permissionsHeader: {
		marginBottom: theme.spacing(0.5),
		color: theme.palette.text.secondary,
		fontSize: "0.8125rem",
	},
	connectionSubPanel: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(0.75),
		marginTop: theme.spacing(0.5),
		marginBottom: theme.spacing(0.5),
		marginLeft: theme.spacing(1),
		paddingLeft: theme.spacing(2),
		borderLeft: `2px solid ${theme.palette.divider}`,
	},
	connectionSubRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: theme.spacing(0.75, 1.25),
		borderRadius: theme.spacing(0.75),
		border: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.background.paper,
	},
	connectionSubLabel: {
		fontSize: "0.8125rem",
		fontWeight: 500,
	},
}));

const UserSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
	password: Yup.string().min(5, "Too Short!").max(50, "Too Long!"),
	email: Yup.string().email("Invalid email").required("Required"),
	allHistoric: Yup.string().nullable(),
});

const CONNECTION_CHANNEL_ITEMS = [
	{ name: "allowConnectionFacebook", labelKey: "userModal.form.allowConnectionFacebook" },
	{ name: "allowConnectionInstagram", labelKey: "userModal.form.allowConnectionInstagram" },
	{ name: "allowConnectionWhatsapp", labelKey: "userModal.form.allowConnectionWhatsapp" },
	{ name: "allowConnectionEmail", labelKey: "userModal.form.allowConnectionEmail" },
];

const PERMISSION_ITEMS = [
	{ name: "allTicket", labelKey: "userModal.form.allTicket", type: "enable" },
	{ name: "allowGroup", labelKey: "userModal.form.allowGroup", type: "boolean" },
	{ name: "allHistoric", labelKey: "userModal.form.allHistoric", type: "enabled" },
	{ name: "allUserChat", labelKey: "userModal.form.allUserChat", type: "enabled" },
	{ name: "userClosePendingTicket", labelKey: "userModal.form.userClosePendingTicket", type: "enabled" },
	{ name: "allowConnections", labelKey: "userModal.form.allowConnections", type: "enabled", isConnectionsMaster: true },
	{ name: "showDashboard", labelKey: "userModal.form.showDashboard", type: "enabled" },
	{ name: "allowRealTime", labelKey: "userModal.form.allowRealTime", type: "enabled" },
	{ name: "allowKanban", labelKey: "userModal.form.allowKanban", type: "enabled" },
	{ name: "allowSchedules", labelKey: "userModal.form.allowSchedules", type: "enabled" },
];

const isPermissionChecked = (value, type) => {
	if (type === "boolean") return value === true;
	if (type === "enable") return value === "enable";
	return value === "enabled";
};

const setPermissionValue = (name, checked, type, setFieldValue) => {
	let value;
	if (type === "boolean") {
		value = checked;
	} else if (type === "enable") {
		value = checked ? "enable" : "disable";
	} else {
		value = checked ? "enabled" : "disabled";
	}

	setFieldValue(name, value);
};

const setConnectionsMaster = (checked, setFieldValue) => {
	setPermissionValue("allowConnections", checked, "enabled", setFieldValue);

	if (!checked) {
		CONNECTION_CHANNEL_ITEMS.forEach((item) => {
			setPermissionValue(item.name, false, "enabled", setFieldValue);
		});
	}
};

const mapConnectionPermissions = (data, defaults) => {
	let allowConnections = data?.allowConnections ?? defaults.allowConnections;
	let allowConnectionFacebook = data?.allowConnectionFacebook ?? defaults.allowConnectionFacebook;
	let allowConnectionInstagram = data?.allowConnectionInstagram ?? defaults.allowConnectionInstagram;
	let allowConnectionWhatsapp = data?.allowConnectionWhatsapp ?? defaults.allowConnectionWhatsapp;
	let allowConnectionEmail = data?.allowConnectionEmail ?? defaults.allowConnectionEmail;

	const hasNewFields =
		data?.allowConnectionFacebook !== undefined ||
		data?.allowConnectionInstagram !== undefined ||
		data?.allowConnectionWhatsapp !== undefined ||
		data?.allowConnectionEmail !== undefined;

	if (!hasNewFields) {
		if (data?.allowMetaConnections === "enabled") {
			allowConnections = "enabled";
			allowConnectionFacebook = "enabled";
			allowConnectionInstagram = "enabled";
			allowConnectionWhatsapp = "disabled";
		} else if (data?.allowConnections === "enabled") {
			allowConnectionFacebook = "enabled";
			allowConnectionInstagram = "enabled";
			allowConnectionWhatsapp = "enabled";
			allowConnectionEmail = "enabled";
		}
	}

	return {
		allowConnections,
		allowConnectionFacebook,
		allowConnectionInstagram,
		allowConnectionWhatsapp,
		allowConnectionEmail,
	};
};

const mapUserFromApi = (data, defaults) => ({
	...defaults,
	...data,
	password: "",
	allowGroup: data?.allowGroup === true || data?.allowGroup === "true",
	allowKanban: data?.allowKanban ?? defaults.allowKanban,
	allowSchedules: data?.allowSchedules ?? defaults.allowSchedules,
	...mapConnectionPermissions(data, defaults),
});

const UserModal = ({ open, onClose, userId }) => {
	const classes = useStyles();

	const initialState = {
		name: "",
		email: "",
		password: "",
		profile: "user",
		startWork: "00:00",
		endWork: "23:59",
		farewellMessage: "",
		allTicket: "disable",
		allowGroup: false,
		defaultTheme: "light",
		defaultMenu: "open",
		allHistoric: "disabled",
		allUserChat: "disabled",
		userClosePendingTicket: "enabled",
		showDashboard: "disabled",
		allowRealTime: "disabled",
		allowConnections: "disabled",
		allowConnectionFacebook: "disabled",
		allowConnectionInstagram: "disabled",
		allowConnectionWhatsapp: "disabled",
		allowConnectionEmail: "disabled",
		allowKanban: "enabled",
		allowSchedules: "enabled",
	};

	const { user: loggedInUser } = useContext(AuthContext);

	const [user, setUser] = useState(initialState);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [whatsappId, setWhatsappId] = useState(false);
	// const [allTicket, setAllTicket] = useState("disable");
	const { loading, whatsApps } = useWhatsApps();
	const [profileUrl, setProfileUrl] = useState(null)
	const [tab, setTab] = useState("general");
	const [avatar, setAvatar] = useState(null);
	const startWorkRef = useRef();
	const endWorkRef = useRef();



	useEffect(() => {
		if (!open) return;

		if (!userId) {
			setUser(initialState);
			setSelectedQueueIds([]);
			setWhatsappId("");
			return;
		}

		let cancelled = false;

		const fetchUser = async () => {
			try {
				const { data } = await api.get(`/users/${userId}`);
				if (cancelled) return;

				setUser(mapUserFromApi(data, initialState));

				const { profileImage } = data;
				setProfileUrl(`${backendUrl}/public/company${data.companyId}/user/${profileImage}`);

				const userQueueIds = data.queues?.map((queue) => queue.id) || [];
				setSelectedQueueIds(userQueueIds);
				setWhatsappId(data.whatsappId ? data.whatsappId : "");
			} catch (err) {
				if (!cancelled) toastError(err);
			}
		};

		fetchUser();

		return () => {
			cancelled = true;
		};
	}, [userId, open]);

	const handleClose = () => {
		onClose();
		setUser(initialState);
		setTab("general");
		setSelectedQueueIds([]);
		setWhatsappId("");
	};

	const handleTabChange = (event, newValue) => {
		setTab(newValue);
	};

	const buildUserPayload = (values) => {
		const payload = {
			name: values.name,
			email: values.email,
			profile: values.profile,
			startWork: values.startWork,
			endWork: values.endWork,
			farewellMessage: values.farewellMessage,
			defaultTheme: values.defaultTheme,
			defaultMenu: values.defaultMenu,
			allTicket: values.allTicket,
			allowGroup: values.allowGroup,
			allHistoric: values.allHistoric,
			allUserChat: values.allUserChat,
			userClosePendingTicket: values.userClosePendingTicket,
			showDashboard: values.showDashboard,
			allowRealTime: values.allowRealTime,
			allowConnections: values.allowConnections,
			allowConnectionFacebook: values.allowConnectionFacebook,
			allowConnectionInstagram: values.allowConnectionInstagram,
			allowConnectionWhatsapp: values.allowConnectionWhatsapp || "disabled",
			allowConnectionEmail: values.allowConnectionEmail || "disabled",
			allowMetaConnections:
				values.allowConnectionFacebook === "enabled" ||
				values.allowConnectionInstagram === "enabled"
					? "enabled"
					: "disabled",
			allowKanban: values.allowKanban,
			allowSchedules: values.allowSchedules,
			whatsappId,
			queueIds: selectedQueueIds,
		};

		if (values.password) {
			payload.password = values.password;
		}

		return payload;
	};

	const handleSaveUser = async (values) => {
		const uploadAvatar = async (file) => {
		  try {
			const formData = new FormData();
			formData.append("userId", file.id);
			formData.append("typeArch", "user");
			formData.append("profileImage", avatar);
	  
			const { data } = await api.post(`/users/${file.id}/media-upload`, formData);
			localStorage.setItem("profileImage", data.user.profileImage);
		  } catch (err) {
			toastError(err);
		  }
		};
	  
		const userData = buildUserPayload(values);
	  
		try {
		  if (userId) {
			const { data } = await api.put(`/users/${userId}`, userData);
			setUser(mapUserFromApi(data, initialState));
	  
			// Corrigido: usar comparação correta e esperar uploadAvatar
			if (avatar && (!user?.profileImage || user?.profileImage !== avatar.name)) {
			  await uploadAvatar(data);
			}
		  } else {
			const { data } = await api.post("/users", userData);
			setUser(mapUserFromApi(data, initialState));
	  
			// Novo usuário, sempre faz upload se avatar existir
			if (avatar) {
			  await uploadAvatar(data);
			}
		  }
	  
		  if (userId === loggedInUser.id) {
			handleClose();
			toast.success(i18n.t("userModal.success"));
			window.location.reload();
		  } else {
			handleClose();
			toast.success(i18n.t("userModal.success"));
		  }
		} catch (err) {
		  toastError(err);
		}
	  };
	  

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="sm"
				fullWidth
				scroll="paper"
			>
				<DialogTitle id="form-dialog-title">
					{userId
						? `${i18n.t("userModal.title.edit")}`
						: `${i18n.t("userModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={user}
					enableReinitialize={true}
					validationSchema={UserSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveUser(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ touched, errors, isSubmitting, setFieldValue, values }) => (
						<Form>
							<Paper className={classes.mainPaper} elevation={1}>
								<Tabs
									value={tab}
									indicatorColor="primary"
									textColor="primary"
									scrollButtons="on"
									variant="scrollable"
									onChange={handleTabChange}
									className={classes.tab}
								>
									<Tab label={i18n.t("userModal.tabs.general")} value={"general"} />
									<Tab label={i18n.t("userModal.tabs.permissions")} value={"permissions"} />
								</Tabs>
							</Paper>
							<Paper className={classes.paper} elevation={0}>
								<DialogContent dividers>
									<TabPanel
										className={classes.container}
										value={tab}
										name={"general"}
									>
										<Grid
											container
											spacing={1}
											alignContent="center"
											alignItems="center"
											justifyContent="center">
											<FormControl className={classes.updateDiv}>
												<AvatarUploader
													setAvatar={setAvatar}
													avatar={user.profileImage}
													companyId={user.companyId}
												/>
												{user.profileImage &&
													<Button
														variant="outlined"
														color="secondary"
														onClick={() => {
															user.profileImage = null;
															setFieldValue("profileImage", null);
															setAvatar(null);
														}}
													>
														{i18n.t("userModal.title.removeImage")}
													</Button>
												}
											</FormControl>
										</Grid>
										<Grid container spacing={1}>
											<Grid item xs={12} md={6} xl={6}>
												<Field
													as={TextField}
													label={i18n.t("userModal.form.name")}
													autoFocus
													name="name"
													error={touched.name && Boolean(errors.name)}
													helperText={touched.name && errors.name}
													variant="outlined"
													margin="dense"
													fullWidth
												/>
											</Grid>
											<Grid item xs={12} md={6} xl={6}>
												<Field
													as={TextField}
													label={i18n.t("userModal.form.password")}
													type="password"
													name="password"
													error={touched.password && Boolean(errors.password)}
													helperText={touched.password && errors.password}
													variant="outlined"
													margin="dense"
													fullWidth
												/>
											</Grid>
										</Grid>
										<Grid container spacing={1}>
											<Grid item xs={12} md={8} xl={8}>
												<Field
													as={TextField}
													label={i18n.t("userModal.form.email")}
													name="email"
													error={touched.email && Boolean(errors.email)}
													helperText={touched.email && errors.email}
													variant="outlined"
													margin="dense"
													fullWidth
												/>
											</Grid>
											<Grid item xs={12} md={4} xl={4}>
												<FormControl
													variant="outlined"
													//className={classes.formControl}
													margin="dense"
													fullWidth
												>
													<Can
														role={loggedInUser.profile}
														perform="user-modal:editProfile"
														yes={() => (
															<>
																<InputLabel id="profile-selection-input-label">
																	{i18n.t("userModal.form.profile")}
																</InputLabel>

																<Field
																	as={Select}
																	label={i18n.t("userModal.form.profile")}
																	name="profile"
																	labelId="profile-selection-label"
																	id="profile-selection"
																	required
																>
																	<MenuItem value="admin">Admin</MenuItem>
																	<MenuItem value="user">User</MenuItem>
																</Field>
															</>
														)}
													/>
												</FormControl>
											</Grid>
										</Grid>
										<Grid container spacing={1}>
											<Grid item xs={12} md={12} xl={12}>
												<Can
													role={loggedInUser.profile}
													perform="user-modal:editQueues"
													yes={() => (
														<QueueSelect
															selectedQueueIds={selectedQueueIds}
															onChange={values => setSelectedQueueIds(values)}
															fullWidth
														/>
													)}
												/>
											</Grid>
										</Grid>
										<Grid container spacing={1}>
											<Grid item xs={12} md={12} xl={12}>
												<Can
													role={loggedInUser.profile}
													perform="user-modal:editProfile"
													yes={() => (
														<FormControl variant="outlined" margin="dense" className={classes.maxWidth} fullWidth>
															<InputLabel>
																{i18n.t("userModal.form.whatsapp")}
															</InputLabel>
															<Field
																as={Select}
																value={whatsappId}
																onChange={(e) => setWhatsappId(e.target.value)}
																label={i18n.t("userModal.form.whatsapp")}

															>
																<MenuItem value={''}>&nbsp;</MenuItem>
																{whatsApps.map((whatsapp) => (
																	<MenuItem key={whatsapp.id} value={whatsapp.id}>{whatsapp.name}</MenuItem>
																))}
															</Field>
														</FormControl>
													)}
												/>
											</Grid>
										</Grid>
										<Can
											role={loggedInUser.profile}
											perform="user-modal:editProfile"
											yes={() => (
												<Grid container spacing={1}>
													<Grid item xs={12} md={6} xl={6}>
														<Field
															as={TextField}
															label={i18n.t("userModal.form.startWork")}
															type="time"
															ampm={"false"}
															inputRef={startWorkRef}
															InputLabelProps={{
																shrink: true,
															}}
															inputProps={{
																step: 600, // 5 min
															}}
															fullWidth
															name="startWork"
															error={
																touched.startWork && Boolean(errors.startWork)
															}
															helperText={
																touched.startWork && errors.startWork
															}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
													</Grid>
													<Grid item xs={12} md={6} xl={6}>
														<Field
															as={TextField}
															label={i18n.t("userModal.form.endWork")}
															type="time"
															ampm={"false"}
															inputRef={endWorkRef}
															InputLabelProps={{
																shrink: true,
															}}
															inputProps={{
																step: 600, // 5 min
															}}
															fullWidth
															name="endWork"
															error={
																touched.endWork && Boolean(errors.endWork)
															}
															helperText={
																touched.endWork && errors.endWork
															}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
													</Grid>
												</Grid>
											)}
										/>

										<Field
											as={TextField}
											label={i18n.t("userModal.form.farewellMessage")}
											type="farewellMessage"
											multiline
											rows={4}
											fullWidth
											name="farewellMessage"
											error={touched.farewellMessage && Boolean(errors.farewellMessage)}
											helperText={touched.farewellMessage && errors.farewellMessage}
											variant="outlined"
											margin="dense"
										/>

										<Grid container spacing={1}>
											<Grid item xs={12} md={6} xl={6}>
												<FormControl
													variant="outlined"
													className={classes.maxWidth}
													margin="dense"
													fullWidth
												>
													<>
														<InputLabel >
															{i18n.t("userModal.form.defaultTheme")}
														</InputLabel>

														<Field
															as={Select}
															label={i18n.t("userModal.form.defaultTheme")}
															name="defaultTheme"
															type="defaultTheme"
															required
														>
															<MenuItem value="light">{i18n.t("userModal.form.defaultThemeLight")}</MenuItem>
															<MenuItem value="dark">{i18n.t("userModal.form.defaultThemeDark")}</MenuItem>
														</Field>
													</>
												</FormControl>
											</Grid>
											<Grid item xs={12} md={6} xl={6}>

												<FormControl
													variant="outlined"
													className={classes.maxWidth}
													margin="dense"
													fullWidth
												>
													<>
														<InputLabel >
															{i18n.t("userModal.form.defaultMenu")}
														</InputLabel>

														<Field
															as={Select}
															label={i18n.t("userModal.form.defaultMenu")}
															name="defaultMenu"
															type="defaultMenu"
															required
														>
															<MenuItem value={"open"}>{i18n.t("userModal.form.defaultMenuOpen")}</MenuItem>
															<MenuItem value={"closed"}>{i18n.t("userModal.form.defaultMenuClosed")}</MenuItem>
														</Field>
													</>
												</FormControl>
											</Grid>
										</Grid>
									</TabPanel>
									<TabPanel
										className={classes.container}
										value={tab}
										name={"permissions"}
									>
										<Can
											role={loggedInUser.profile}
											perform="user-modal:editProfile"
											yes={() => (
												<>
													<Typography className={classes.permissionsHeader}>
														{i18n.t("userModal.permissions.hint")}
													</Typography>
													<div className={classes.permissionsPanel}>
														{PERMISSION_ITEMS.map((item) => {
															const checked = isPermissionChecked(values[item.name], item.type);
															return (
																<React.Fragment key={item.name}>
																	<div
																		className={`${classes.permissionRow} ${checked ? classes.permissionRowActive : ""}`}
																	>
																		<div className={classes.permissionLabel}>
																			<Typography className={classes.permissionTitle}>
																				{i18n.t(item.labelKey)}
																			</Typography>
																		</div>
																		<Checkbox
																			color="primary"
																			checked={checked}
																			onChange={(event) => {
																				if (item.isConnectionsMaster) {
																					setConnectionsMaster(
																						event.target.checked,
																						setFieldValue
																					);
																					return;
																				}
																				setPermissionValue(
																					item.name,
																					event.target.checked,
																					item.type,
																					setFieldValue
																				);
																			}}
																			inputProps={{
																				"aria-label": i18n.t(item.labelKey),
																			}}
																		/>
																	</div>
																	{item.isConnectionsMaster && checked && (
																		<div className={classes.connectionSubPanel}>
																			{CONNECTION_CHANNEL_ITEMS.map((channel) => {
																				const channelChecked = isPermissionChecked(
																					values[channel.name],
																					"enabled"
																				);
																				return (
																					<div
																						key={channel.name}
																						className={classes.connectionSubRow}
																					>
																						<Typography className={classes.connectionSubLabel}>
																							{i18n.t(channel.labelKey)}
																						</Typography>
																						<Checkbox
																							color="primary"
																							size="small"
																							checked={channelChecked}
																							onChange={(event) =>
																								setPermissionValue(
																									channel.name,
																									event.target.checked,
																									"enabled",
																									setFieldValue
																								)
																							}
																							inputProps={{
																								"aria-label": i18n.t(channel.labelKey),
																							}}
																						/>
																					</div>
																				);
																			})}
																		</div>
																	)}
																</React.Fragment>
															);
														})}
													</div>
												</>
											)}
										/>
									</TabPanel>
								</DialogContent>
							</Paper>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("userModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{userId
										? `${i18n.t("userModal.buttons.okEdit")}`
										: `${i18n.t("userModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div >
	);
};

export default UserModal;
